// react
import React, { forwardRef } from 'react';

// utils
import { cn } from './cn';

export type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded';
export type SkeletonAnimation = 'pulse' | 'wave' | 'none';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual shape of the skeleton placeholder. */
  variant?: SkeletonVariant;
  /** Width as number (px) or string (e.g. '100%'). */
  width?: string | number;
  /** Height as number (px) or string (e.g. '1rem'). */
  height?: string | number;
  /** Animation style. */
  animation?: SkeletonAnimation;
}

const variantClasses: Record<SkeletonVariant, string> = {
  text: 'rounded',
  circular: 'rounded-full',
  rectangular: 'rounded-none',
  rounded: 'rounded-lg',
};

/**
 * Skeleton — Placeholder block used while content is loading.
 * Supports `text | circular | rectangular | rounded` shapes and `pulse | wave | none` animations.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      variant = 'text',
      width,
      height,
      animation = 'pulse',
      style,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(
          // brand-aware muted surface (Archie-UI token)
          'bg-[var(--color-gray-100)]',
          variantClasses[variant],
          animation === 'pulse' && 'animate-pulse',
          animation === 'wave' &&
            'overflow-hidden relative before:absolute before:inset-0 before:translate-x-[-100%] before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent',
          className,
        )}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          ...style,
        }}
        {...props}
      />
    );
  },
);

Skeleton.displayName = 'Skeleton';

// -------------------------------------------------------------------------
// Preset compositions
// -------------------------------------------------------------------------

export interface SkeletonTextProps extends Omit<SkeletonProps, 'variant'> {
  /** Number of text lines to render. */
  lines?: number;
}

/** Multi-line text skeleton. Last line is automatically narrower for natural feel. */
export const SkeletonText = forwardRef<HTMLDivElement, SkeletonTextProps>(
  ({ lines = 1, className, ...props }, ref) => {
    if (lines === 1) {
      return (
        <Skeleton
          ref={ref}
          variant="text"
          height={16}
          className={className}
          {...props}
        />
      );
    }

    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            height={16}
            width={i === lines - 1 ? '75%' : '100%'}
            {...props}
          />
        ))}
      </div>
    );
  },
);

SkeletonText.displayName = 'SkeletonText';

export interface SkeletonAvatarProps extends Omit<SkeletonProps, 'variant'> {
  /** Avatar diameter in px. */
  size?: number;
}

/** Circular avatar skeleton. */
export const SkeletonAvatar = forwardRef<HTMLDivElement, SkeletonAvatarProps>(
  ({ size = 40, ...props }, ref) => {
    return (
      <Skeleton
        ref={ref}
        variant="circular"
        width={size}
        height={size}
        {...props}
      />
    );
  },
);

SkeletonAvatar.displayName = 'SkeletonAvatar';

export type SkeletonCardProps = Omit<SkeletonProps, 'variant'>;

/** Card-shaped skeleton: avatar + title/subtitle + 3-line text block. */
export const SkeletonCard = forwardRef<HTMLDivElement, SkeletonCardProps>(
  ({ className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-4 space-y-4', className)}>
        <div className="flex items-center gap-3">
          <SkeletonAvatar size={40} {...props} />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" height={16} width="40%" {...props} />
            <Skeleton variant="text" height={12} width="60%" {...props} />
          </div>
        </div>
        <SkeletonText lines={3} {...props} />
      </div>
    );
  },
);

SkeletonCard.displayName = 'SkeletonCard';
